import { isMapEntry } from '@/components/blocks/AddressCard/Main';
import { useZipCodeInput } from '@/components/content/Header/parts/DeliveryLocator/hooks/useZipCodeInput';
import {
	locationOptionAddressDetailSX,
	locationOptionAliasSX,
	locationOptionEditSX,
	locationOptionFavoriteAddressSX,
	locationOptionIconContainerSX,
	locationOptionSelectBtnSX,
} from '@/components/content/Header/parts/DeliveryLocator/styles/LocationOption/locationOptionStyles';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { Address } from '@/data/types/Address';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { JavaUtilMapEntry } from 'integration/generated/transactions/data-contracts';
import { useContext } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';
import { formatExteriorNumberWithHash } from 'shared/custom/utils/exteriorNumber';

type Props = {
	address: Address;
	handleOnEditAddress: (address: Address) => void;
	handleCloseDeliveryPanel: () => void;
};

export const LocationOption = ({
	address,
	handleOnEditAddress,
	handleCloseDeliveryPanel,
}: Props) => {
	const localization = useLocalization('DeliveryLocator');
	const addressLocalization = useLocalization('AddressCard');
	const {
		nickName,
		primary,
		addressLine1,
		addressLine2,
		addressLine3,
		city,
		zipCode,
		attributes,
		email1,
	} = address;
	const addressField3 = attributes?.find(
		(att): att is JavaUtilMapEntry => isMapEntry(att) && att.key === 'addressField3'
	);

	const isAddressAccount = nickName === email1;
	const exteriorNumber = formatExteriorNumberWithHash(addressLine2);

	const addressDetail = addressField3
		? `${addressLine1} ${exteriorNumber}, ${addressField3.value}, Col. ${addressLine3}, ${city}, C.P. ${zipCode}`
		: `${addressLine1} ${exteriorNumber}, Col. ${addressLine3}, ${city}, C.P. ${zipCode}`;

	const isFavoriteAddress = primary && JSON.parse(primary);

	const { handleSelectAddress } = useContext(ContentContext) as ReturnType<typeof useZipCodeInput>;

	const handleOnSelectAddress = () => {
		handleSelectAddress(address);
		handleCloseDeliveryPanel();
	};

	return (
		<Stack gap={1.25}>
			{isAddressAccount ? (
				<Typography variant="bodyBaseline" sx={{ color: 'grey.500'}}>
					{addressLocalization.AccountAddress.t()}
				</Typography>
			) : (
				<Typography variant="bodyBaseline" sx={locationOptionAliasSX}>
					{nickName}
				</Typography>
			)}
			{isFavoriteAddress ? (
				<Typography variant="bodySubText" sx={locationOptionFavoriteAddressSX}>
					{localization.Labels.favoriteAddress.t()}
				</Typography>
			) : null}
			<Box sx={locationOptionAddressDetailSX}>
				<DynamicIcon
					name={MATERIAL_SYMBOL_ICONS.locationOn}
					variant="filled"
					sx={locationOptionIconContainerSX}
				/>
				<Typography variant="bodySubText">{addressDetail.toLowerCase()}</Typography>
			</Box>
			<Link sx={locationOptionEditSX} onClick={() => handleOnEditAddress(address)}>
				<Typography variant="bodyBaseline">{localization.edit.t()}</Typography>
			</Link>
			<Button
				variant="outlined"
				fullWidth
				sx={locationOptionSelectBtnSX}
				onClick={handleOnSelectAddress}
			>
				<Typography variant="bodyBaseline">{localization.selectLocation.t()}</Typography>
			</Button>
		</Stack>
	);
};
